import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, CalendarDays, Clock, User } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { Chip, SampleTag, StoryCard } from "../components/cards";
import ShareButtons from "../components/ShareButtons";
import { getStoryBySlug, getRelatedStories, formatDate } from "../lib/content";

function BodyBlock({ block }) {
  switch (block.type) {
    case "heading":
      return (
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink mt-14 mb-5">
          {block.text}
        </h2>
      );
    case "quote":
      return (
        <blockquote className="my-12 border-l-2 border-action pl-6 md:pl-8 font-serif italic text-2xl md:text-3xl leading-snug text-ink">
          {block.text}
        </blockquote>
      );
    case "image":
      return (
        <figure className="my-12">
          <div className="overflow-hidden rounded-xl">
            <img src={block.src} alt={block.alt} loading="lazy" className="w-full object-cover" />
          </div>
          {block.caption && (
            <figcaption className="mt-3 text-xs text-ink-2">{block.caption}</figcaption>
          )}
        </figure>
      );
    default:
      return (
        <p className="text-base md:text-lg leading-relaxed text-ink-2 mb-7">{block.text}</p>
      );
  }
}

export default function StoryDetail() {
  const { slug } = useParams();
  const story = getStoryBySlug(slug);

  if (!story) {
    return (
      <main className="pt-40 pb-32 text-center" data-testid="story-not-found">
        <h1 className="font-serif text-4xl text-ink">Story not found</h1>
        <Link to="/stories" className="mt-6 inline-block text-action font-semibold underline underline-offset-4">
          Back to all stories
        </Link>
      </main>
    );
  }

  const related = getRelatedStories(story, 2);

  return (
    <main>
      <SEO title={story.title} description={story.excerpt} path={`/stories/${slug}`} image={story.image} />

      <article className="pt-32 md:pt-40 pb-24" data-testid="story-detail">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/stories"
            data-testid="story-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-ink-2 hover:text-ink transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> All stories
          </Link>

          <header className="mt-10 max-w-3xl">
            <div className="flex flex-wrap items-center gap-3">
              <Chip pillar={story.category} />
              {story.isSample && <SampleTag />}
            </div>
            <h1 className="mt-5 font-serif tracking-tighter leading-[1.02] text-ink text-4xl sm:text-5xl lg:text-6xl">
              {story.title}
            </h1>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-2">
              <span className="inline-flex items-center gap-2">
                <CalendarDays className="h-4 w-4" /> {formatDate(story.publishedAt, { weekday: "long" })}
              </span>
              <span className="inline-flex items-center gap-2">
                <Clock className="h-4 w-4" /> {story.readingTime} min read
              </span>
              {story.author && (
                <span className="inline-flex items-center gap-2">
                  <User className="h-4 w-4" /> {story.author}
                </span>
              )}
            </div>
          </header>

          <Reveal className="mt-12">
            <div className="overflow-hidden rounded-xl aspect-[21/9]">
              <img src={story.image} alt={story.imageAlt} className="h-full w-full object-cover" />
            </div>
          </Reveal>

          <div className="mt-14 max-w-2xl mx-auto">
            {story.isSample && (
              <p className="mb-10 border border-line rounded-xl bg-sand-2 p-5 text-sm text-ink-2" data-testid="story-sample-notice">
                This is a sample story written to demonstrate the layout of
                PilCoo's Stories section. It is not an organisational claim or
                report.
              </p>
            )}
            {story.body.map((block, i) => (
              <BodyBlock key={i} block={block} />
            ))}

            <div className="mt-14 pt-8 border-t border-line flex flex-wrap items-center justify-between gap-6">
              <ShareButtons title={story.title} />
            </div>

            <div className="mt-12 rounded-xl bg-ink text-sand p-8 md:p-10" data-testid="story-end-cta">
              <h2 className="font-serif text-2xl md:text-3xl tracking-tight">
                Want to be part of the next story?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-sand/70">
                The best stories start with someone showing up. Join an activity,
                or tell us about a need you've seen.
              </p>
              {story.endCta ? (
                <a
                  href={story.endCta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${story.endCta.label} (opens in new tab)`}
                  data-testid="story-cta"
                  className="group mt-6 inline-flex items-center gap-2 rounded-full bg-sand text-ink px-6 py-3 text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                >
                  {story.endCta.label}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ) : (
                <Link
                  to="/get-involved"
                  data-testid="story-cta"
                  className="group mt-6 inline-flex items-center gap-2 rounded-full bg-sand text-ink px-6 py-3 text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                >
                  Get involved
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              )}
            </div>
          </div>

          {related.length > 0 && (
            <section className="mt-20" data-testid="story-related">
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">Keep reading</h2>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                {related.map((s) => (
                  <StoryCard key={s.slug} story={s} withImage />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </main>
  );
}
